import Cookies from 'js-cookie';

import {
  IMPERSONATION_METADATA,
  IMPERSONATION_ORIGINAL_ACCESS_TOKEN,
  IMPERSONATION_ORIGINAL_REFRESH_TOKEN,
  IMPERSONATION_REFRESH_TOKEN,
  IMPERSONATION_SESSION_ID,
  ImpersonationMetadata
} from '@/shared/config/impersonation.config';
import { USER_ACCESS_TOKEN, USER_REFRESH_TOKEN } from '@/shared/config/cookies.config';
import {
  cleanupUserData,
  setUserToken
} from '@/shared/services/helpers/auth-helper/auth-helper.service';
import { UserAuthUIModel } from '@/shared/ui-models/user-auth.ui-model';
import jwt_decode from 'jwt-decode';
import { impersonationApiService } from '@/Modules/UserManagement/shared/services/impersonation-api.service';

export function isImpersonating(): boolean {
  return !!sessionStorage.getItem(IMPERSONATION_SESSION_ID);
}

export function getImpersonationSessionId(): string | null {
  return sessionStorage.getItem(IMPERSONATION_SESSION_ID);
}

export function getImpersonationMetadata(): ImpersonationMetadata | null {
  const raw = sessionStorage.getItem(IMPERSONATION_METADATA);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationMetadata;
  } catch {
    return null;
  }
}

export function getImpersonationRefreshToken(): string | null {
  return sessionStorage.getItem(IMPERSONATION_REFRESH_TOKEN);
}

export function beginImpersonationSession(params: {
  accessToken: string;
  refreshToken: string;
  impersonationRefreshToken: string;
  impersonationSessionId: string;
  tokenExpiresIn: number;
  metadata: ImpersonationMetadata;
}): void {
  const originalAccessToken = Cookies.get(USER_ACCESS_TOKEN);
  const originalRefreshToken = Cookies.get(USER_REFRESH_TOKEN);

  if (originalAccessToken && originalAccessToken !== 'undefined') {
    sessionStorage.setItem(IMPERSONATION_ORIGINAL_ACCESS_TOKEN, originalAccessToken);
  }
  if (originalRefreshToken && originalRefreshToken !== 'undefined') {
    sessionStorage.setItem(IMPERSONATION_ORIGINAL_REFRESH_TOKEN, originalRefreshToken);
  }

  sessionStorage.setItem(IMPERSONATION_SESSION_ID, params.impersonationSessionId);
  sessionStorage.setItem(IMPERSONATION_REFRESH_TOKEN, params.impersonationRefreshToken);
  sessionStorage.setItem(IMPERSONATION_METADATA, JSON.stringify(params.metadata));

  const { email, userId, role, permissions, name } = jwt_decode<UserAuthUIModel>(
    params.accessToken
  );

  setUserToken({
    accessToken: params.accessToken,
    refreshToken: params.impersonationRefreshToken,
    tokenExpiresIn: params.tokenExpiresIn,
    email,
    userId,
    role,
    permissions,
    name
  });
}

export async function stopImpersonationSession(): Promise<void> {
  const sessionId = getImpersonationSessionId();
  if (sessionId) {
    try {
      await impersonationApiService.stop(sessionId);
    } catch (error) {
      console.error('[impersonation] stop session failed', error);
    }
  }

  const originalAccessToken = sessionStorage.getItem(IMPERSONATION_ORIGINAL_ACCESS_TOKEN);
  const originalRefreshToken = sessionStorage.getItem(IMPERSONATION_ORIGINAL_REFRESH_TOKEN);

  clearImpersonationStorage();

  if (originalAccessToken && originalRefreshToken) {
    const { email, userId, role, permissions, name } =
      jwt_decode<UserAuthUIModel>(originalAccessToken);
    setUserToken({
      accessToken: originalAccessToken,
      refreshToken: originalRefreshToken,
      tokenExpiresIn: 365,
      email,
      userId,
      role,
      permissions,
      name
    });
  } else {
    cleanupUserData();
  }
}

export function clearImpersonationStorage(): void {
  sessionStorage.removeItem(IMPERSONATION_SESSION_ID);
  sessionStorage.removeItem(IMPERSONATION_REFRESH_TOKEN);
  sessionStorage.removeItem(IMPERSONATION_ORIGINAL_ACCESS_TOKEN);
  sessionStorage.removeItem(IMPERSONATION_ORIGINAL_REFRESH_TOKEN);
  sessionStorage.removeItem(IMPERSONATION_METADATA);
}

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

type MutationConfirmDetail = { method: string; url: string };

let pendingConfirm: {
  resolve: (allowed: boolean) => void;
  detail: MutationConfirmDetail;
} | null = null;

export function requestImpersonationMutationConfirm(
  detail: MutationConfirmDetail
): Promise<boolean> {
  if (pendingConfirm) {
    pendingConfirm.resolve(false);
  }

  return new Promise((resolve) => {
    pendingConfirm = { resolve, detail };
    window.dispatchEvent(
      new CustomEvent('impersonation-mutation-confirm', {
        detail
      })
    );
  });
}

export function resolveImpersonationMutationConfirm(allowed: boolean): void {
  if (!pendingConfirm) return;
  pendingConfirm.resolve(allowed);
  pendingConfirm = null;
}

export function isMutatingHttpMethod(method?: string, url?: string): boolean {
  const lowerMethod = (method || 'get').toLowerCase();

  // Treat POST /search as read-only (like GET)
  if (lowerMethod === 'post' && url?.includes('/search')) {
    return false;
  }

  return MUTATING_METHODS.has(lowerMethod);
}

export function attachImpersonationHeader(headers: Record<string, string>): void {
  const sessionId = getImpersonationSessionId();
  if (sessionId) {
    headers['x-impersonation-session-id'] = sessionId;
  }
}
